import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { blogAPI } from '../lib/api';
import { ArrowRight, Clock, Eye } from 'lucide-react';

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await blogAPI.getAll();
        setPosts(res.data);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <div data-testid="blog-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Insights</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Blog & Updates</h1>
          <p className="text-muted-foreground max-w-2xl">
            Industry analysis, ecosystem updates, and thought leadership from 
            Portland's technology community.
          </p>
        </div>
      </section>

      {/* Posts */}
      <section>
        <div className="container-wide py-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="border border-border p-6 animate-pulse">
                  <div className="h-40 bg-muted rounded mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`blog-post-${post.slug}`}
                >
                  <Card className="card-hover h-full">
                    <CardContent className="p-0">
                      {post.image_url && (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={post.image_url}
                            alt={post.title}
                            className="w-full h-full object-cover grayscale-hover"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                          {post.created_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(post.created_at).toLocaleDateString()}
                            </span>
                          )}
                          {post.view_count > 0 && (
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {post.view_count}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            by {post.author}
                          </span>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <h3 className="font-semibold text-lg mb-2">No posts yet</h3>
              <p className="text-sm text-muted-foreground">
                Check back soon for updates and insights.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
